import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { api, errorText } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { SkeletonList } from '../components/ui/Skeleton';
import ErrorState from '../components/ui/ErrorState';
import Modal from '../components/ui/Modal';
import {
  Kanban,
  Plus,
  Building2,
  GripVertical,
  Trash2,
} from 'lucide-react';

interface Application {
  id: number;
  job_id: number | null;
  company_name: string;
  job_title: string;
  status: string;
  applied_at: string | null;
  response_at: string | null;
  interview_at: string | null;
  notes: string;
  application_url: string;
  contact_name: string;
  contact_email: string;
  salary_offered: string;
  created_at: string;
  updated_at: string;
}

interface AppForm {
  company_name: string;
  job_title: string;
  status: string;
  application_url: string;
  notes: string;
  contact_name: string;
  contact_email: string;
  salary_offered: string;
}

const COLUMNS = [
  { key: 'saved', label: 'Saved', color: 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600' },
  { key: 'applied', label: 'Applied', color: 'bg-primary-100 text-primary-700 border-primary-300 dark:bg-primary-900/40 dark:text-primary-300 dark:border-primary-700' },
  { key: 'phone_screen', label: 'Phone Screen', color: 'bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-700' },
  { key: 'interview', label: 'Interview', color: 'bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-700' },
  { key: 'offer', label: 'Offer', color: 'bg-green-100 text-green-700 border-green-300 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700' },
  { key: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700' },
];

const emptyForm: AppForm = {
  company_name: '', job_title: '', status: 'saved',
  application_url: '', notes: '', contact_name: '', contact_email: '', salary_offered: '',
};

const Tracker: React.FC = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<AppForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const { confirm } = useConfirm();

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<{ applications?: Application[] }>('/api/tracker', { retry: 2 });
      setApplications(data.applications || []);
    } catch (e) {
      setError(errorText(e, 'Failed to load applications'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchApplications(); }, [fetchApplications]);

  const openAdd = (status: string = 'saved') => {
    setEditingId(null);
    setForm({ ...emptyForm, status });
    setShowModal(true);
  };

  const openEdit = (app: Application) => {
    setEditingId(app.id);
    setForm({
      company_name: app.company_name,
      job_title: app.job_title,
      status: app.status,
      application_url: app.application_url || '',
      notes: app.notes || '',
      contact_name: app.contact_name || '',
      contact_email: app.contact_email || '',
      salary_offered: app.salary_offered || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.company_name.trim() || !form.job_title.trim()) return;
    setSaving(true);
    try {
      if (editingId) await api.put(`/api/tracker/${editingId}`, form);
      else await api.post('/api/tracker', form);
      setShowModal(false);
      await fetchApplications();
      toast.success(editingId ? 'Application updated' : 'Application added');
    } catch (e) {
      toast.error(errorText(e, 'Failed to save application'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number, title: string) => {
    const ok = await confirm({
      title: 'Remove application',
      message: `Remove "${title}" from your tracker?`,
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.del(`/api/tracker/${id}`);
      await fetchApplications();
      toast.success('Application removed');
    } catch (e) {
      toast.error(errorText(e, 'Failed to delete'));
    }
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      await api.patch(`/api/tracker/${id}/status`, { status: newStatus });
      await fetchApplications();
    } catch (e) {
      toast.error(errorText(e, 'Failed to update status'));
    }
  };

  const getColumnApps = (status: string) =>
    applications.filter((a) => a.status === status);

  const formatDate = (d: string | null) => {
    if (!d) return '';
    return new Date(d + (d.endsWith('Z') ? '' : 'Z')).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Kanban className="w-8 h-8 text-primary-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Application Tracker</h1>
            <p className="text-gray-600 dark:text-slate-400 mt-1">Track your job applications through the pipeline</p>
          </div>
        </div>
        <button
          onClick={() => openAdd()}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Application
        </button>
      </div>

      {loading ? (
        <SkeletonList rows={4} />
      ) : error ? (
        <ErrorState message={error} onRetry={fetchApplications} retrying={loading} />
      ) : (
        /* Kanban Board */
        <div className="flex gap-4 overflow-x-auto pb-4">
          {COLUMNS.map((col) => {
            const colApps = getColumnApps(col.key);
            return (
              <div key={col.key} className="flex-shrink-0 w-64">
                <div className={`px-3 py-2 rounded-t-xl border-b-2 ${col.color} flex items-center justify-between`}>
                  <span className="text-sm font-semibold">{col.label}</span>
                  <span className="text-xs font-medium opacity-75">{colApps.length}</span>
                </div>
                <div className="bg-gray-50 dark:bg-slate-800/50 rounded-b-xl min-h-[200px] p-2 space-y-2">
                  {colApps.map((app, i) => (
                    <motion.div
                      key={app.id}
                      className="bg-white dark:bg-navy-800 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm p-3 cursor-pointer hover:shadow-md transition-shadow"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03 }}
                      onClick={() => openEdit(app)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{app.job_title}</p>
                          <p className="text-xs text-gray-500 dark:text-slate-400 flex items-center mt-0.5">
                            <Building2 className="w-3 h-3 mr-1" />{app.company_name}
                          </p>
                        </div>
                        <GripVertical className="w-3.5 h-3.5 text-gray-300 dark:text-slate-500 flex-shrink-0 mt-0.5" />
                      </div>
                      {app.applied_at && (
                        <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-1.5">Applied {formatDate(app.applied_at)}</p>
                      )}
                      {app.salary_offered && (
                        <p className="text-[10px] text-green-600 font-medium mt-0.5">{app.salary_offered}</p>
                      )}
                      {/* Quick move buttons */}
                      <div className="flex items-center space-x-1 mt-2">
                        {COLUMNS.filter((c) => c.key !== app.status).slice(0, 3).map((c) => (
                          <button
                            key={c.key}
                            onClick={(e) => { e.stopPropagation(); handleStatusChange(app.id, c.key); }}
                            className={`px-1.5 py-0.5 text-[10px] rounded border ${c.color} hover:opacity-80`}
                            title={`Move to ${c.label}`}
                          >
                            {c.label.split(' ')[0]}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                  <button
                    onClick={() => openAdd(col.key)}
                    className="w-full py-2 text-xs text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl border border-dashed border-gray-300 dark:border-slate-700 transition-colors"
                  >
                    <Plus className="w-3 h-3 inline mr-1" />Add
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingId ? 'Edit Application' : 'Add Application'}
        footer={
          <>
            {editingId && (
              <button
                onClick={() => { setShowModal(false); handleDelete(editingId, form.job_title); }}
                className="mr-auto flex items-center rounded-xl px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <Trash2 className="mr-1 h-4 w-4" />Delete
              </button>
            )}
            <button
              onClick={() => setShowModal(false)}
              className="rounded-xl bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !form.company_name.trim() || !form.job_title.trim()}
              className="rounded-xl bg-primary-600 px-4 py-2 text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : editingId ? 'Update' : 'Add Application'}
            </button>
          </>
        }
      >
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Company *</label>
                  <input
                    type="text"
                    value={form.company_name}
                    onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-slate-800 dark:text-white dark:placeholder-slate-500"
                    placeholder="e.g. Anthropic"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Job Title *</label>
                  <input
                    type="text"
                    value={form.job_title}
                    onChange={(e) => setForm({ ...form, job_title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-slate-800 dark:text-white dark:placeholder-slate-500"
                    placeholder="e.g. Senior Software Engineer"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-slate-800 dark:text-white"
                  >
                    {COLUMNS.map((c) => (
                      <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                    <option value="withdrawn">Withdrawn</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Salary Offered</label>
                  <input
                    type="text"
                    value={form.salary_offered}
                    onChange={(e) => setForm({ ...form, salary_offered: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-slate-800 dark:text-white dark:placeholder-slate-500"
                    placeholder="e.g. $180k"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Application URL</label>
                <input
                  type="url"
                  value={form.application_url}
                  onChange={(e) => setForm({ ...form, application_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-slate-800 dark:text-white dark:placeholder-slate-500"
                  placeholder="https://..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Contact Name</label>
                  <input
                    type="text"
                    value={form.contact_name}
                    onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-slate-800 dark:text-white dark:placeholder-slate-500"
                    placeholder="Recruiter name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Contact Email</label>
                  <input
                    type="email"
                    value={form.contact_email}
                    onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-slate-800 dark:text-white dark:placeholder-slate-500"
                    placeholder="recruiter@company.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none bg-white dark:bg-slate-800 dark:text-white dark:placeholder-slate-500"
                  placeholder="Any notes about this application..."
                />
              </div>
            </div>

      </Modal>
    </div>
  );
};

export default Tracker;
